import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import imageCardAmerican from '@/assets/images/cards/americanexpress.jpg';
import imageCardBlank from '@/assets/images/cards/blank.png';
import imageCardDiscover from '@/assets/images/cards/discover.jpg';
import imageCardMasterCard from '@/assets/images/cards/mastercard.jpg';
import imageCardVisa from '@/assets/images/cards/visa.jpg';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import { formMixin, validators } from '@/components/forms/mixin';
import notificationIcon from '@/components/notification-icon/notification-icon.vue';
import depositAmountOverview from '@/components/wallet/shared/deposit-amount-overview/deposit-amount-overview.vue';
import depositAmountPicker from '@/components/wallet/shared/deposit-amount-picker/deposit-amount-picker.vue';
import depositDisclaimer from '@/components/wallet/shared/deposit-disclaimer/deposit-disclaimer.vue';
import depositDoneLinks from '@/components/wallet/shared/deposit-done-links/deposit-done-links.vue';
import depositInfoOverview from '@/components/wallet/shared/deposit-info-overview/deposit-info-overview.vue';
import depositResult from '@/components/wallet/shared/deposit-result/deposit-result.vue';
import { DEPOSIT_METHOD_TYPE } from '@/components/wallet/shared/depositMethodTypes';
import feeStatusMessage from '@/components/wallet/shared/fee-status-message/fee-status-message.vue';
import feeWaivedBanner from '@/components/wallet/shared/fee-waived-banner/fee-waived-banner.vue';
import promoCodeInput from '@/components/wallet/shared/promo-code-input/promo-code-input.vue';
import constants from '@/constants';
import { withMeta } from '@/helpers/dataLayer';
import getValidationMessage from '@/helpers/getValidationMessage';
import accountVerifyMixin from '@/mixins/accountVerify';
import contentLoaderMixin from '@/mixins/contentLoader';
import depositWarningMixin from '@/mixins/depositWarningMixin';
import documentUploadMixin from '@/mixins/documentUpload';
import loaderMixin from '@/mixins/loader';
import popupPageModeMixin from '@/mixins/popupPageModeMixin';
import promoCodeMixin from '@/mixins/promoCode';
import { isWrapperApp } from '@/mixins/userAgent';
import { getCardTypeForNumber, isNumber, roundToTwo } from '@/utils/helpers';

const DEPOSIT_CHIP_AMOUNTS = [10, 25, 50];

const STEPS = {
  INITIAL: 1,
  CONFIRM: 2,
  TRY_AGAIN: 3,
  SUCCESS: 4,
};

const CARD_IMAGES = {
  [constants.CARDS.AMERICAN_EXPRESS]: imageCardAmerican,
  [constants.CARDS.DISCOVER]: imageCardDiscover,
  [constants.CARDS.MASTERCARD]: imageCardMasterCard,
  [constants.CARDS.VISA]: imageCardVisa,
};

export default {
  mixins: [
    accountVerifyMixin,
    contentLoaderMixin,
    documentUploadMixin,
    formMixin,
    loaderMixin,
    popupPageModeMixin,
    promoCodeMixin,
    depositWarningMixin,
  ],
  validations() {
    const rules = {
      fields: {
        name: validators.required,
        number: validators.cardNumber,
        expiryDateMonth: validators.required,
        expiryDateYear: validators.required,
        cvv: validators.cvv,
        amount: validators.depositAmountCoinbase(
          this.minimumDepositAmount,
          this.maximumDepositAmount,
        ),
      },
    };

    if (this.avsFlag) {
      rules.fields.address = validators.required;
      rules.fields.city = validators.required;
      rules.fields.zipCode = validators.validateUSZipCode('country');
    }
    return rules;
  },
  components: {
    notificationIcon,
    depositAmountPicker,
    promoCodeInput,
    depositInfoOverview,
    depositResult,
    depositAmountOverview,
    feeWaivedBanner,
    feeStatusMessage,
    depositDisclaimer,
    depositDoneLinks,
  },
  data() {
    return {
      depositMethodType: DEPOSIT_METHOD_TYPE.CREDIT_CARD,
      canDeposit: false,
      step: STEPS.INITIAL,
      years: constants.expiryYears,
      months: constants.months,
      depositAmounts: DEPOSIT_CHIP_AMOUNTS,
      errorMessage: '',
      errorMessageDepositFail: '',
      avsMessage: false,
      avsFlag: false,
      processingFeePercent: 0,
      processingFeeAmount: '0.00',
      totalToWallet: '0.00',
      wasFeeDiscountApplied: false,
      feeFreeAmount: 0,
      feeDiscountEligibility: false,
      feeFreeCap: 0,
      fields: {
        name: '',
        amount: '',
        number: '',
        expiryDateMonth: '',
        expiryDateYear: '',
        cvv: '',
        address: '',
        city: '',
        zipCode: '',
      },
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    isWrapperApp() {
      return isWrapperApp;
    },
    ...mapGetters('app', [
      'isLayoutDesktop',
      'isLayoutMobile',
      'isDepositPromoInputBoxEnabled',
      'collegeAffiliationEnabled',
    ]),
    ...mapState('wallet', ['currentDeposit', 'minimumDepositAmount', 'currentDeposit']),
    ...mapState('user', ['status', 'userData', 'ssnInfoVerified', 'twoFactorEnabled']),

    classModifiers() {
      return {
        'add-funds-credit-card': true,
        'add-funds-credit-card--popup': this.isPopup,
        'add-funds-credit-card--regular': !this.isPopup,
      };
    },

    availableCardTypes() {
      return (this.method.subTypes || []).map((i) => i.toLowerCase());
    },

    maximumDepositAmount() {
      return 99999;
    },

    accountUploadDocumentTheme() {
      return this.isPage ? 'page-padded' : '';
    },
    amountHasError() {
      return this.$v.fields.amount.$error;
    },

    amountValidationMessage() {
      return getValidationMessage(this.$v.fields.amount);
    },

    cardNumber() {
      return (this.fields.number || '').replace(/ /g, '');
    },

    cardType() {
      return getCardTypeForNumber(this.cardNumber);
    },

    cardTypeHasError() {
      return (
        this.cardType !== constants.CARDS.BLANK && !this.availableCardTypes.includes(this.cardType)
      );
    },

    cardTypeValidationMessage() {
      return 'This card is not supported';
    },

    cardTypeImage() {
      return CARD_IMAGES[this.cardType] || imageCardBlank;
    },

    isConfirmDisabled() {
      return this.invalidOrDisabled || !this.canDeposit || this.cardTypeHasError;
    },

    depositPayload() {
      const payload = {
        cardHoldersName: this.fields.name,
        amount: this.fields.amount,
        cardNumber: this.cardNumber,
        securityCode: this.fields.cvv,
        expirationMonth: this.fields.expiryDateMonth.value,
        expirationYear: this.fields.expiryDateYear.value,
        depositPromoCode: this.promoApplied ? this.promoAppliedCode : '',
        marketingSource: '',
        marketingProgram: '',
        cardType: this.cardType,
      };
      if (this.avsFlag) {
        payload.address1 = this.fields.address;
        payload.city = this.fields.city;
        payload.postalCode = this.fields.zipCode;
      }
      return payload;
    },
    cardMasked() {
      const num = this.cardNumber;
      const last4 = num.length >= 4 ? num.slice(-4) : num;
      const type = this.cardType;
      const label =
        type && type !== constants.CARDS.BLANK
          ? type.charAt(0).toUpperCase() + type.slice(1)
          : 'Card';
      return last4 ? `${label} ••• ${last4}` : label;
    },

    successAmount() {
      const amount = this.currentDeposit ? this.currentDeposit.amount || 0 : 0;
      return `$${Number(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  methods: {
    ...mapActions('wallet', [
      'getDepositCard',
      'confirmDepositCard',
      'submitDepositCard',
      'setCurrentDeposit',
      'getBalance',
      'getDepositMethods',
    ]),
    ...mapActions('popup', [
      'setGenericPopup',
      'setCurrentPopup',
      'setCurrentSmallPopup',
      'setCurrentPopupOptions',
      'setCurrentPopupWithDelay',
    ]),
    initialize() {
      this.loaderAdd();
      this.getBalance()
        .then(this.getDepositCard)
        .then((data) => {
          this.loaderRemove();
          this.isComponentReady = true;
          this.checkDocumentUploadFlags(data);
          this.canDeposit = data.canDeposit;
          this.errorMessage = data.errorMessage;
          this.promoCodeDetail = data.promoCodeDetail || {};
          this.fields.name = data.cardHoldersName;
          this.avsFlag = data.avsFlag;
          this.fields.city = data.city || '';
          this.fields.address = data.address1 || '';
          this.fields.zipCode = data.postalCode || '';
          this.fields.country = { value: this.userData.countryCode };
          this.processingFeePercent = roundToTwo((data.feePercent || 0) * 100);
          this.feeDiscountEligibility = !!data.feeDiscountEligibility;
          this.feeFreeCap = Number(data.feeDiscountCap || 0);
        })
        .catch((error) => {
          this.isComponentReady = true;
          this.errorMessage = get(error, 'response.data.message', null);
          this.loaderRemove();
          this.checkDocumentUploadFlags(get(error, 'response.data', {}));
        });

      this.getDepositMethods();
    },

    scrollToTopIfPage() {
      if (this.isLayoutMobile && this.isPage) {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      }
    },

    onUpdateAmount(amount) {
      if (this.fields.amount === '') {
        this.fields.amount = amount.toFixed(2);
      } else if (isNumber(this.fields.amount)) {
        this.fields.amount = (Number(this.fields.amount) + amount).toFixed(2);
      }
      this.$gtm.trackEvent(
        withMeta({
          event: 'eventTrackerTrigger',
          category: 'Deposit',
          action: 'click',
          label: `Amount ${amount} Button`,
        }),
      );
    },

    handleAmountBlur() {
      this.$v.fields.amount.$touch();
      this.$gtm.trackEvent(
        withMeta({
          event: 'eventTrackerTrigger',
          category: 'Deposit',
          action: 'fill',
          label: 'Amount',
        }),
      );
    },

    onConfirmDeposit() {
      this.avsMessage = false;
      this.$gtm.trackEvent(
        withMeta({
          event: 'eventTrackerTrigger',
          category: 'Deposit',
          action: 'submit',
          label: 'Submit Deposit',
        }),
      );

      if (this.formIsValid() && this.formDisable()) {
        this.loaderAdd();
        this.confirmDepositCard(this.depositPayload)
          .then((data) => {
            this.loaderRemove();
            this.formEnable();
            if (this.checkDocumentUploadFlags(data)) return;
            if (data.dapFlag) {
              this.step = STEPS.INITIAL;
              this.canDeposit = false;
              this.errorMessage = data.dapMessage;
            } else if (data.avsFlag) {
              this.step = STEPS.INITIAL;
              this.avsFlag = true;
              this.avsMessage = true;
              this.getDepositCard()
                .then((depositData) => {
                  this.checkDocumentUploadFlags(depositData);
                  this.fields.city = depositData.city || '';
                  this.fields.address = depositData.address1 || '';
                  this.fields.zipCode = depositData.postalCode || '';
                  this.fields.country = { value: this.userData.countryCode };
                })
                .catch((error) => {
                  this.errorMessage = get(error, 'response.data.message', null);
                });
            } else {
              this.processingFeePercent = roundToTwo((data.feePercent || 0) * 100);
              this.processingFeeAmount = Number(Math.abs(data.fee || 0)).toFixed(2);
              this.totalToWallet = Number(data.totalAmount || 0).toFixed(2);
              this.wasFeeDiscountApplied = !!data.wasFeeDiscountApplied;
              this.feeFreeAmount = Number(data.feeFreeAmount || 0);
              this.promotionalCreditAmount = data.promoAmount;
              this.step = STEPS.CONFIRM;
              this.scrollToTopIfPage();
            }
          })
          .catch((error) => {
            this.loaderRemove();
            if (this.checkDocumentUploadFlags(get(error, 'response.data', {}))) return;
            this.formEnable();
            this.handleDepositFail(error);
          });
      }
    },

    onSubmitDeposit() {
      this.formDisable();
      this.$gtm.trackEvent(
        withMeta({
          event: 'eventTrackerTrigger',
          category: 'Deposit Confirm',
          action: 'submit',
          label: 'Confirm Deposit',
        }),
      );
      this.loaderAdd();
      this.submitDepositCard(this.depositPayload)
        .then((data) => {
          this.loaderRemove();
          if (this.checkDocumentUploadFlags(data)) return;
          if (typeof data.feeFreeAmount !== 'undefined') {
            this.feeFreeAmount = Number(data.feeFreeAmount || 0);
          }
          this.setCurrentDeposit(data);
          this.$gtm.trackEvent(
            withMeta({
              event: this.status.hasBeenFunded ? 'MultiDeposit' : 'Deposit',
            }),
          );
          this.formEnable();
          this.$emit('success');
          this.step = STEPS.SUCCESS;
        })
        .then(this.getBalance)
        .catch((error) => {
          this.loaderRemove();
          if (this.checkDocumentUploadFlags(get(error, 'response.data', {}))) return;
          this.formEnable();
          this.handleDepositFail(error);
        });
    },

    handleDepositFail(error) {
      this.errorMessageDepositFail = get(error, 'response.data.message', '');
      this.step = STEPS.TRY_AGAIN;
    },

    onBack() {
      this.step = STEPS.INITIAL;
      this.errorMessageDepositFail = '';
    },

    onUseDifferentCard() {
      this.fields.number = '';
      this.fields.expiryDateYear = '';
      this.fields.expiryDateMonth = '';
      this.fields.cvv = '';
      this.$v.fields.number.$reset();
      this.$v.fields.expiryDateMonth.$reset();
      this.$v.fields.expiryDateYear.$reset();
      this.$v.fields.cvv.$reset();
      this.errorMessageDepositFail = '';
      this.step = STEPS.INITIAL;
    },

    onStartFromScratch() {
      this.onResetPromoCode();
      this.onUseDifferentCard();
      this.fields.amount = '';
      this.errorMessageDepositFail = '';
      this.step = STEPS.INITIAL;
      this.initialize();
    },

    onSuccessDone() {
      this.getDepositMethods().then(async () => {
        // Wallet defaults to Deposit, so return to Dashboard before showing follow-up prompts.
        if (this.isPage && isWrapperApp) {
          await this.$router.push({ name: 'portfolio' });
        }
        let shownPopup = false;
        if (this.collegeAffiliationEnabled && this.status && this.status.isUCAffilSet === null) {
          shownPopup = true;
          this.setCurrentPopupWithDelay({ name: MODALS.COLLEGE_AFFILIATION });
        } else if (this.twoFactorEnabled !== true && !constants.DISABLE_TWO_FACTOR_PROMPT) {
          shownPopup = true;
          this.setCurrentPopup(MODALS.TWO_FACTOR_PROMPT);
        }
        if (this.isPage) {
          if (!isWrapperApp) this.onStartFromScratch();
        } else if (!shownPopup) {
          this.setCurrentPopup(null);
        }
      });
    },
    showCVVModal() {
      this.setCurrentSmallPopup(MODALS.SMALL_CVV);
    },

    isStep(stepName) {
      return this.step === STEPS[stepName];
    },

    fieldName(name) {
      return `${this.mode}-${name}`;
    },
  },
  watch: {
    ssnInfoVerified() {
      this.initialize();
    },
  },
};
